import { Injectable } from '@angular/core';
import { ILanguages } from './interfaces/ILanguages';

/**
 * TODO: These should be move to constants.ts
 */
@Injectable({
    providedIn: 'root',
})
export class Constants {
    // Those settings will be stored in the localStorage
    public readonly pref = {
        VIDEO_QUALITY: 'video_quality', // Setting from 1 (low) to 4 (very high)
        LANGUAGE: 'language', // UI language "en", "de", "system"
        TRANSLATION_TYPE: 'translation_type', // "0" = default, "1" = word-by-word
        TRANSLATION_LANG: 'translation_lang', // Preferred translation language, e.g. "en"
        WATCHED_VIDEOS: 'watched_videos', // An array of WatchedVideos objects
        FAVORED_VIDEOS: 'favored_videos', // An array of WatchedVideos objects
        VIDEO_SOURCE: 'video_source', // See VIDEO_SOURCE (eg. ORFTVTHEK, NDR)
        WORD_CATALOG: 'word_catalog',
        WORD_CATALOG_CLASSES: 'word_catalog_classes', // Word catalog specific for each class
        INSTALLATION_ID: 'installation_id',
        FIRST_LAUNCH_TIMESTAMP: 'first_launch_timestamp',
        PLAYBACK_RATE_INDEX: 'playback_rate_index',
        PLAYER_HIDE_SUBTITLES: 'player_hide_subtitles', // '0' or undefined = show subtitles, '1' = hide subtitles
        LOGGED_USER: 'logged_user',
        LOGGED_USER_CUSTOMER: 'logged_user_customer',
        ONLY_KIDS_CONTENT: 'only_kids_content',
        LOGGED_USER_PREFERENCES: 'logged_user_preferences',
        USER_EMAIL: 'user_email',
        USER_EMAIL_ENCRYPTED: 'user_email_encrypted',
        USER_LANGUAGE_LEVEL_DE: 'user_language_level_de',
        USER_LANGUAGE_LEVEL_EN: 'user_language_level_en',
        AUTH_TOKEN: 'auth_token',
        TERMS_ACCEPTED: 'terms_accepted',
        TUTORIAL_DONE: 'tutorial_done',
        OPENED_CLASSES: 'opened_classes', // An array of the classes accessed by user
        LICENSE: 'license', // License of logged user which has restrictions
        EVENTS: 'events', // An array of events which will be saved to server within 10 minutes
        REACHALL_INTRO_HIDE: 'reachall_intro_hide', // Will be set to the the time at which the intro was skipped (ISO date string)
        INVITED_EDUCATORS: 'invited_educators',
        INVITE_EDUCATORS_CLOSED: 'invite_educators_closed', // How many times the "close" button on the invite-colleagues page was clicked
        PLAYER_VOLUME: 'player_volume', // Video player volume - indicates the current audio level as a percentage in decimal form. This means that 1 is 100%, 0.5 is 50%, and so on.
        PLAYER_MUTED: 'player_muted', // Video player is muted? 1 = muted, 0 = not muted,
        FEEDBACK_BUTTON_CLICKED_BKA2021: 'feedback_button_clicked_bka2021',
        SUBTITLE_FONT_SIZE: 'subtitle_font_size',
        VIDEO_CLICK_DELAY: 'video_click_delay',
        HIDE_VIDEO_INTRO_OVERLAY: 'hide_video_intro_overlay',
        LANGUAGE_SELECTION_SHOWN: 'language_selection_shown',
        ASK_APP_INSTALLATION_TIMESTAMP: 'ask_app_installation_timestamp',
        ONBOARDING_STUDENT_DONE: 'onboarding_student_done',
        ONBOARDING_EDUCATOR_DONE: 'onboarding_educator_done',
        ONBOARDING_REDIRECT_URL: 'onboarding_redirect_url',
    };

    public readonly VIDEO_SOURCE = {
        ALL: '',
        ORFTVTHEK: 'orftvthek',
        NDR: 'ndr',
        DASERSTE: 'daserste'
    };

    public readonly DefaultVideoQuality = 'medium';
    public readonly DefaultLanguage = 'system';
    public readonly DefaultTranslationType = '0';
    public readonly DefaultTranslationLang = 'en';
    public readonly DefaultTranslationLangFallbackEn = 'de';
    public readonly DefaultVideoSource: string = this.VIDEO_SOURCE.ORFTVTHEK;
    public readonly DefaultPlaybackRateIndex = '3';
    public readonly DefaultSubtitleFontSize = 'medium';

    public readonly DefaultVideoClickDelay = '0';

    public readonly AzureBlobContainer = 'appassessments';

    /**
     * If true, then the word catalog will be saved in localStorage (no sentence detection!).
     * If false, then the word catalog will be saved on the server via the API endpoints.
     */
    public SaveWordCatalogLocally = false;

    // public VideoSourcesEnabledForNonEducators = [this.VIDEO_SOURCE.ORFTVTHEK];

    /* Preparing text from Word (Save as ... HTML):
        Remove: "text-align:justify;"
    */

    public readonly GeneralClassId = 'general';

    /**
     * How many seconds we take at the beginning and and of a video, so we don't consider it "watched" or "watched completely".
     */
    public readonly VideoWatchedThresholdSeconds = 5;

    /**
     * How many seconds the player will go back when resuming a video after pausing (or continuing to watch).
     */
    public readonly VideoGoBackAfterResumeSeconds = 2.0

    /**
     * How many times an educator will be asked to invite colleagues.
     */
    public readonly AskEducatorInviteMaxTimes = 4;

    /**
     * https://developer.mozilla.org/de/docs/Web/HTTP/Status
     */
    public readonly HTTP_STATUS_CODE = {
        UNAUTHORIZED: 401
    };

    public readonly TermsTextDe = `
<div class='terms-of-use-alert'>

<h4>Nutzungsbedingungen für die Verwendung der App uugot.it</h4>

<p><span style=''>Anbieter der App ist die </span><span lang=DE
style='mso-fareast-font-family:"Times New Roman";'>uugot.it GmbH</span><span lang=UZ-CYR
style='mso-fareast-font-family:"Times New Roman";mso-ansi-language:UZ-CYR'> (im Folgenden „</span><span
lang=DE style=''>uugot.it</span><span lang=UZ-CYR style='mso-fareast-font-family:"Times New Roman";
mso-ansi-language:UZ-CYR'>“).</span></p>

<p><span style=''>Die Nutzung der App setzt voraus, dass Sie den
nachfolgenden Nutzungsbedingungen zustimmen. Diese Nutzungsbedingungen regeln das Verhältnis
zwischen Ihnen und uugot.it im Zusammenhang mit der Nutzung der App. Bitte lesen
Sie sich diese deshalb sorgfältig durch.</span></p>

<p style='mso-margin-top-alt:auto;mso-margin-bottom-alt:auto;
mso-outline-level:3'><b><span lang=UZ-CYR
style='font-size:13.5pt;mso-fareast-font-family:
"Times New Roman";mso-ansi-language:UZ-CYR'>1. Allgemeines zur
Nutzung und Nutzungsrecht</span></b></p>

<p><span style=''>uugot.it stellt eine Plattform zur Verfügung, über
die Fernsehsendungen, Berichte, Videomaterial und ähnliche Streaming-Inhalte Dritter
um Lerninhalte erweitert werden.</span></p>

<p><span style=''>uugot.it enthält Verweise zu Internet-Seiten
Dritter (sogenannte „Links“). Durch die Verwendung eines Links befindet sich
der User auf der Internet-Seite eines Dritten. uugot.it übernimmt keinerlei
Verantwortung oder Haftung für die Verfügbarkeit, Richtigkeit, Vollständigkeit
und Aktualität der Informationen des Dritten. uugot.it steht damit insbesondere
nicht dafür ein, dass spezifische Fernsehsendungen, Berichte oder Materialien
für Sie erreichbar sind. uugot.it distanziert sich ausdrücklich von allfälligen
rechtswidrigen Inhalten auf Seiten Dritter und wird bei Kenntnis einer
rechtswidrigen Tätigkeit oder Information und bei Kenntnis von Tatsachen oder
Umständen, aus denen eine rechtswidrige Tätigkeit oder Information
offensichtlich wird, unverzüglich tätig werden, um den Link zu entfernen. </span></p>

<p><span style=''>Die Nutzung der App ist nur für den persönlichen
Gebrauch gestattet, eine gewerbliche Nutzung bedarf des ausdrücklichen
Einverständnisses von uugot.it. </span></p>

<p><span style=''>Mit Download und/oder Nutzung der App wird Ihnen
ein nicht-ausschließliches, nicht übertragbares, nicht unterlizenzierbares und jederzeit
und frei widerrufliches Nutzungsrecht an der App eingeräumt und Sie erwerben
keinerlei darüber hinausgehenden Rechte, insbesondere keine Urheber- oder
gewerblichen Schutzrechte.</span></p>

<p><span style=''>Die App und deren Funktionen dürfen nicht in
missbräuchlicher Art und Weise verwendet werden. Bitte nutzen Sie die App nur
im gesetzlich zulässigen Rahmen und entsprechend unserer Nutzungsbedingungen.
Bei Verstoß gegen geltendes österreichisches Recht oder unsere
Nutzungsbedingungen behält sich uugot.it das Recht vor, Sie von der Nutzung der
App auszuschließen.</span></p>

<p style='mso-margin-top-alt:auto;mso-margin-bottom-alt:auto;
mso-outline-level:3'><b><span lang=DE
style='font-size:13.5pt;mso-fareast-font-family:
"Times New Roman";'>2</span></b><b><span style='font-size:13.5pt;'>. Haftung</span></b></p>

<p><span style=''>uugot.it übernimmt keinerlei Gewährleistung hinsichtlich
Verfügbarkeit, Zuverlässigkeit, Funktionalität der App oder der Eignung der App
für Ihre Zwecke oder hinsichtlich der Verfügbarkeit von Inhalten. </span></p>

<p><span style=''>Eine Haftung ist, soweit nicht nach dem
Produkthaftungsgesetz, wegen Vorsatzes, </span><span style='
mso-ansi-language:DE-AT;'>krass </span><span style='font-size:12.0pt;'>grober Fahrlässigkeit, oder wegen Verletzung des
Lebens, des Körpers oder Gesundheit</span><span style='font-size:
12.0pt;mso-ansi-language:DE-AT;'> </span><span style=''>zwingend gehaftet wird, ausgeschlossen.</span></p>

<p style='mso-margin-top-alt:auto;mso-margin-bottom-alt:auto;
mso-outline-level:3'><b><span
style='font-size:13.5pt;mso-fareast-font-family:"Times New Roman";mso-ansi-language:DE-AT'>3</span></b><b><span
style='font-size:13.5pt;mso-ansi-language:UZ-CYR'>. Datenschutz</span></b></p>

<p><span style=''>Es gilt die Datenschutzerklärung
(<a href="https://www.uugot.it/datenschutz.html" target="_blank">www.uugot.it/datenschutz.html</a>)
von uugot.it.</span></p>

<p style='mso-margin-top-alt:auto;mso-margin-bottom-alt:auto;
mso-outline-level:3'><b><span lang=DE
style='font-size:13.5pt;mso-fareast-font-family:
"Times New Roman";'>4</span></b><b><span style='font-size:13.5pt;'>. Sonstiges</span></b></p>

<p><span style=''>uugot.it arbeitet stetig daran, seine Dienste zu
optimieren. Deswegen behält sich uugot.it das Recht vor, Funktionen und
Features hinzuzufügen oder zu entfernen und eventuell neue Beschränkungen der
Dienste einzuführen. Sie können jederzeit die Nutzung der App beenden. </span></p>

<p><span style=''>uugot.it behält sich das Recht vor, die
Nutzungsbedingungen zu ändern und anzupassen. Hierüber werden Sie in geeigneter
Weise informiert.</span></p>

</div>
`;

    public readonly LicensesText = `Open Source Acknowledgements

The following sets forth attribution notices for third party software that may be contained in portions of the uugot.it apps.
We thank the open source community for all their contributions.

---
RxJS

Copyright (c) 2015-2018 Google, Inc., Netflix, Inc., Microsoft Corp. and contributors

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.

---
video.js

Copyright Brightcove, Inc.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.

---
email-validator

This is free and unencumbered software released into the public domain.

Anyone is free to copy, modify, publish, use, compile, sell, or
distribute this software, either in source code form or as a compiled
binary, for any purpose, commercial or non-commercial, and by any
means.

In jurisdictions that recognize copyright laws, the author or authors
of this software dedicate any and all copyright interest in the
software to the public domain. We make this dedication for the benefit
of the public at large and to the detriment of our heirs and
successors. We intend this dedication to be an overt act of
relinquishment in perpetuity of all present and future rights to this
software under copyright law.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS BE LIABLE FOR ANY CLAIM, DAMAGES OR
OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE,
ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
OTHER DEALINGS IN THE SOFTWARE.

For more information, please refer to <http://unlicense.org>

---
jwt-decode

The MIT License (MIT)

Copyright (c) 2015 Auth0, Inc. <support@auth0.com> (http://auth0.com)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

---
NGX Logger

The MIT License

Copyright (c) 2018 David Fannin

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

---
Popper

The MIT License (MIT)

Copyright (c) 2019 Federico Zivolo

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

---
shuffle-array

The MIT License (MIT)

Copyright (c) 2014 Guille Paz

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

---
ngx-translate

Copyright (c) 2018 Olivier Combe

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

---
angular2-uuid

The MIT License (MIT)

Copyright (c) 2016 Wulf Solter

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

---
ts-md5

The MIT License (MIT)

---
PhoneGap / Cordova Social Sharing plugin

The MIT License (MIT)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

---
Cordova AppVersion plugin

Copyright (c) 2013 White October

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

---
Ionic

Copyright 2015-present Drifty Co.
http://drifty.com/

MIT License

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

---
Apache Cordova

Copyright 2012 The Apache Software Foundation

This product includes software developed at
The Apache Software Foundation (http://www.apache.org/).

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.

---
cordova-plugin-androidx

The MIT License

Copyright (c) 2019 Dave Alden / Working Edge Ltd.

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

---
PromisesPlugin

The MIT License (MIT)

Copyright (c) 2014-15 Vlad Stirbu

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

---
videojs-offset

Copyright (c) Carles Galan Cladera <cgcladera@gmail.com>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

---
Moment.js

MIT License

Copyright (c) JS Foundation and other contributors

Permission is hereby granted, free of charge, to any person
obtaining a copy of this software and associated documentation
files (the "Software"), to deal in the Software without
restriction, including without limitation the rights to use,
copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the
Software is furnished to do so, subject to the following
conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
OTHER DEALINGS IN THE SOFTWARE.

---
Ionic4 DatePicker

The MIT License

Copyright Logistic Infotech

This software consists of voluntary contributions made by many
individuals. For exact contribution history, see the revision history
available at https://github.com/logisticinfotech/ionic4-datepicker

The following license applies to all parts of this software except as
documented below:

====

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

    `;
}
